import apiClient from "../lib/api-client";

export interface PurchaseReportFilters {
  startDate?: string;
  endDate?: string;
  storeId?: string;
  supplierId?: string;
  status?: string;
}

export interface PurchaseReportResponse {
  orders: Array<{
    id: string;
    poNumber: string;
    orderDate: string;
    status: string;
    supplier: string;
    store: string;
    itemCount: number;
    subtotal: number;
    totalDiscount: number;
    totalGst: number;
    grandTotal: number;
  }>;
  summary: {
    totalOrders: number;
    totalSubtotal: number;
    totalDiscount: number;
    totalGst: number;
    totalAmount: number;
  };
}

export interface SupplierOutstandingResponse {
  supplierId: string;
  supplierName: string;
  phone: string | null;
  email: string | null;
  totalPurchases: number;
  totalPayments: number;
  outstanding: number;
  lastPaymentDate: string | null;
}

export interface DayBookEntry {
  id: string;
  date: string;
  type: string;
  reference: string;
  party: string;
  amount: number;
  direction: "IN" | "OUT";
  paymentMethod: string;
  notes?: string;
}

export interface DayBookReportData {
  summary: {
    totalSales: number;
    totalReturns: number;
    totalCustomerReceipts: number;
    totalSupplierPayments: number;
    netInflow: number;
    cashIn: number;
    cashOut: number;
  };
  entries: DayBookEntry[];
}

export interface CashBookEntry {
  id: string;
  date: string;
  type: string;
  reference: string;
  party: string;
  amount: number;
  direction: "IN" | "OUT";
  paymentMethod: string;
  notes?: string;
}

export interface CashBookReportData {
  summary: {
    openingBalance: number;
    totalCashIn: number;
    totalCashOut: number;
    closingBalance: number;
  };
  entries: CashBookEntry[];
}

export interface CounterReportData {
  summary: Array<{
    counterId: string;
    counterName: string;
    sessionCount: number;
    invoiceCount: number;
    totalSales: number;
    totalReturns: number;
    totalCashIn: number;
    totalCashOut: number;
    totalCard: number;
    totalUPI: number;
    totalCredit: number;
    expectedCash: number;
    actualCash: number;
    variance: number;
  }>;
  sessions: Array<{
    sessionId: string;
    sessionNumber: string;
    sessionDate: string;
    counterId: string | null;
    counterName: string;
    totalSales: number;
    totalReturns: number;
    totalCashIn: number;
    totalCashOut: number;
    totalCard: number;
    totalUPI: number;
    totalCredit: number;
    expectedCash: number;
    actualCash: number;
    variance: number;
    invoiceCount: number;
    status: string;
  }>;
}

export interface EndOfDayReportData {
  summary: {
    totalSales: number;
    totalReturns: number;
    totalCashIn: number;
    totalCashOut: number;
    totalCard: number;
    totalUPI: number;
    totalCredit: number;
    expectedCash: number;
    actualCash: number;
    variance: number;
    invoiceCount: number;
  };
  sessions: Array<{
    sessionId: string;
    sessionNumber: string;
    sessionDate: string;
    counterName: string;
    status: string;
    openingCash: number;
    expectedCash: number;
    actualCash: number;
    variance: number;
    totalSales: number;
    totalReturns: number;
    totalCashIn: number;
    totalCashOut: number;
    totalCard: number;
    totalUPI: number;
    totalCredit: number;
    invoiceCount: number;
  }>;
}

class ReportsService {
  async getPurchaseReport(
    filters: PurchaseReportFilters,
  ): Promise<PurchaseReportResponse> {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.storeId) params.append("storeId", filters.storeId);
    if (filters.supplierId) params.append("supplierId", filters.supplierId);
    if (filters.status) params.append("status", filters.status);

    const response = await apiClient.get(
      `/reports/purchases?${params.toString()}`,
    );
    return response.data;
  }

  async getSupplierOutstanding(): Promise<SupplierOutstandingResponse[]> {
    const response = await apiClient.get("/reports/supplier-outstanding");
    return response.data;
  }

  async getDayBookReport(filters: {
    startDate?: string;
    endDate?: string;
    storeId?: string;
  }): Promise<DayBookReportData> {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.storeId) params.append("storeId", filters.storeId);

    const response = await apiClient.get(
      `/reports/day-book?${params.toString()}`,
    );
    return response.data;
  }

  async getCashBookReport(filters: {
    startDate?: string;
    endDate?: string;
    storeId?: string;
  }): Promise<CashBookReportData> {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.storeId) params.append("storeId", filters.storeId);

    const response = await apiClient.get(
      `/reports/cash-book?${params.toString()}`,
    );
    return response.data;
  }

  async getCounterReport(filters: {
    startDate?: string;
    endDate?: string;
    storeId?: string;
    counterId?: string;
  }): Promise<CounterReportData> {
    const params = new URLSearchParams();
    if (filters.startDate) params.append("startDate", filters.startDate);
    if (filters.endDate) params.append("endDate", filters.endDate);
    if (filters.storeId) params.append("storeId", filters.storeId);
    if (filters.counterId) params.append("counterId", filters.counterId);

    const response = await apiClient.get(
      `/reports/counter?${params.toString()}`,
    );
    return response.data;
  }

  async getEndOfDayReport(filters: {
    date?: string;
    storeId?: string;
    counterId?: string;
  }): Promise<EndOfDayReportData> {
    const params = new URLSearchParams();
    if (filters.date) params.append("date", filters.date);
    if (filters.storeId) params.append("storeId", filters.storeId);
    if (filters.counterId) params.append("counterId", filters.counterId);

    const response = await apiClient.get(
      `/reports/end-of-day?${params.toString()}`,
    );
    return response.data;
  }
}

export const reportsService = new ReportsService();
